import { bootstrapAuth, configureAuth } from '@realtyfeed/auth';
import { assertRequiredEnv, createApiClient } from '@realtyfeed/data-access';
import { initI18n } from '@realtyfeed/i18n';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App';
import './styles/index.css';

/**
 * Startup, in the one order that works.
 *
 * 1. `createApiClient` — installs the singleton; nothing can make a request before it exists.
 * 2. `configureAuth` — Amplify has to be configured before any token can be asked for.
 * 3. `initI18n` — the shell's chrome reads it during its first render.
 * 4. `bootstrapAuth` — resolves the session AND fetches the permission grant, so the route
 *    guards and the sidebar have an answer other than "unknown" on the first frame. Awaited:
 *    rendering first means every guard sees `'unknown'` and every gated nav row is hidden
 *    until this resolves, which reads as a broken sidebar rather than a loading one.
 */
const bootstrap = async () => {
  assertRequiredEnv(import.meta.env);

  createApiClient({
    baseURL: import.meta.env.REACT_APP_PUBLIC_PATH,
    apiKey: import.meta.env.REACT_APP_API_KEY,
  });

  configureAuth({ runMode: import.meta.env.REACT_APP_RUN_MODE });

  initI18n();

  await bootstrapAuth();

  const container = document.getElementById('root');
  if (!container) throw new Error('Root element #root not found in index.html');

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
};

/** A failed bootstrap would otherwise leave a blank page and one console line. */
const showStartupError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  const target = document.getElementById('root') ?? document.body;
  const pre = document.createElement('pre');
  pre.setAttribute('role', 'alert');
  pre.className = 'm-6 whitespace-pre-wrap text-sm text-red-700';
  pre.textContent = message;
  target.replaceChildren(pre);
};

bootstrap().catch(showStartupError);
